import { SegmentedToggle } from '@/components/pages/calendar/facet-filter';
import { MENU_GROUPS, MENU_ITEMS, PERMISSION_MODULES } from '@/lib/permissions';

const LEVEL_SEGMENTS = [
  { id: 'none', label: 'None' },
  { id: 'view', label: 'View' },
  { id: 'manage', label: 'Manage' },
];

const VISIBILITY_SEGMENTS = [
  { id: 'show', label: 'Show' },
  { id: 'hide', label: 'Hide' },
];

/**
 * The function matrix of one access group: every app module as a row with a uniform
 * None / View / Manage segmented control ("none" is meaningful everywhere — it hides the
 * area entirely; manage implies view server-side, so no combinations are needed).
 * `value` is the wire map {module: "view"|"manage"} — none is expressed by absence.
 */
export function PermissionMatrix({ value = {}, onChange, disabled = false }) {
  // Emit a per-module updater rather than a map built from the render-time `value`
  // prop — two level changes in the same React batch would otherwise clobber each
  // other (the second click's map wouldn't include the first click's change).
  const setLevel = (moduleKey, level) => {
    onChange?.((current) => {
      const next = { ...(current || {}) };
      if (level === 'none') {
        delete next[moduleKey];
      } else {
        next[moduleKey] = level;
      }
      return next;
    });
  };

  return (
    <div className="divide-y divide-border rounded-lg border border-border bg-card">
      {PERMISSION_MODULES.map((module) => (
        <div key={module.key} className="flex items-center gap-4 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-foreground">{module.label}</div>
            <div className="text-[11px] leading-snug text-muted-foreground">{module.description}</div>
          </div>
          <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
            <SegmentedToggle
              value={value[module.key] || 'none'}
              segments={LEVEL_SEGMENTS}
              onChange={(level) => setLevel(module.key, level)}
              ariaLabel={`${module.label} access level`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Per-item Show/Hide switches for the nav sidebar and the Settings rail, grouped by surface.
 * Cosmetic trimming on top of the function matrix above — an item stays reachable via its
 * module rights either way; hiding just removes it from the member's menu (hidden in ANY of a
 * person's groups wins; admins always see everything). `value` is the hidden-key array;
 * onChange gets a functional updater for the same batching reason as PermissionMatrix.
 *
 * Grouping is RENDER-ONLY: the stored array is still sorted through the flat MENU_ITEMS, which
 * is the canonical wire order. Sorting through MENU_GROUPS instead would let the two orders
 * drift apart.
 */
export function MenuVisibilityList({ value = [], onChange, disabled = false }) {
  const setVisibility = (menuKey, visibility) => {
    onChange?.((current) => {
      const hidden = new Set(current || []);
      if (visibility === 'hide') {
        hidden.add(menuKey);
      } else {
        hidden.delete(menuKey);
      }
      return MENU_ITEMS.filter((m) => hidden.has(m.key)).map((m) => m.key);
    });
  };

  return (
    <div className="divide-y divide-border rounded-lg border border-border bg-card">
      {MENU_GROUPS.map((section) => (
        <div key={section.group} className="divide-y divide-border">
          <div className="bg-muted/40 px-4 py-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-muted-foreground">
            {section.group}
          </div>
          {section.items.map((item) => (
            <div key={item.key} className="flex items-center gap-4 px-4 py-2.5">
              <div className="min-w-0 flex-1 text-[13px] font-semibold text-foreground">{item.label}</div>
              <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
                <SegmentedToggle
                  value={value.includes(item.key) ? 'hide' : 'show'}
                  segments={VISIBILITY_SEGMENTS}
                  onChange={(visibility) => setVisibility(item.key, visibility)}
                  ariaLabel={`${item.label} menu visibility`}
                />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Per-clinic Show/Hide switches for the Messages clinic-tab row (WhatsApp numbers). Same
 * cosmetic-trimming model as MenuVisibilityList — hiding a clinic just removes its tab for the
 * group's members (the "All" tab still includes its threads; admins see every tab). Unlike the
 * menu list the vocabulary is dynamic tenant config, so `clinics` (`[{botId, label}]`) is
 * passed in rather than a static constant. `value` is the hidden-botId array; onChange gets a
 * functional updater for the same batching reason as PermissionMatrix.
 */
export function ClinicVisibilityList({ clinics = [], value = [], onChange, disabled = false }) {
  const setVisibility = (botId, visibility) => {
    onChange?.((current) => {
      const hidden = new Set(current || []);
      if (visibility === 'hide') {
        hidden.add(botId);
      } else {
        hidden.delete(botId);
      }
      // Keep only ids still present in the tenant's clinic list, in that list's order.
      return clinics.filter((c) => hidden.has(c.botId)).map((c) => c.botId);
    });
  };

  if (clinics.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-3 text-[12px] text-muted-foreground">
        No WhatsApp clinic numbers are configured for this workspace, so there are no clinic tabs to hide.
      </p>
    );
  }

  return (
    <div className="divide-y divide-border rounded-lg border border-border bg-card">
      {clinics.map((clinic) => (
        <div key={clinic.botId} className="flex items-center gap-4 px-4 py-2.5">
          <div className="min-w-0 flex-1 text-[13px] font-semibold text-foreground">{clinic.label}</div>
          <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
            <SegmentedToggle
              value={value.includes(clinic.botId) ? 'hide' : 'show'}
              segments={VISIBILITY_SEGMENTS}
              onChange={(visibility) => setVisibility(clinic.botId, visibility)}
              ariaLabel={`${clinic.label} clinic tab visibility`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

const CHAT_DIRECTORY_SEGMENTS = [
  { id: 'visible', label: 'Visible' },
  { id: 'hidden', label: 'Hidden' },
];

/**
 * Single opt-in switch for the internal-chat directory: whether this group's members
 * appear in the Chats "+ New chat" people picker (and can be added to chat groups).
 * Default HIDDEN — visibility is granted per group (visible in ANY group wins; admins
 * are always visible). Unlike the hide-lists above this is one boolean, not a per-key
 * list. `value` is the boolean; onChange receives the next boolean directly.
 */
export function ChatDirectoryVisibilityRow({ value = false, onChange, disabled = false }) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-foreground">Visible in chat directory</div>
          <div className="text-[11px] leading-snug text-muted-foreground">
            Members show up in the New chat people picker and can be added to chat groups.
          </div>
        </div>
        <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
          <SegmentedToggle
            value={value ? 'visible' : 'hidden'}
            segments={CHAT_DIRECTORY_SEGMENTS}
            onChange={(next) => onChange?.(next === 'visible')}
            ariaLabel="Chat directory visibility"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Short "4 manage · 3 view · 5 hidden" summary for group list rows. Pass
 * `{ includeHidden: false }` to drop the trailing hidden count — the employee table summarises
 * a person's effective rights, where "5 hidden" reads as noise. Returns "" for no permissions.
 */
export function permissionsSummary(permissions = {}, { includeHidden = true } = {}) {
  const manage = Object.values(permissions).filter((l) => l === 'manage').length;
  const view = Object.values(permissions).filter((l) => l === 'view').length;
  const parts = [];
  if (manage > 0) parts.push(`${manage} manage`);
  if (view > 0) parts.push(`${view} view`);
  if (includeHidden) parts.push(`${PERMISSION_MODULES.length - manage - view} hidden`);
  return parts.join(' · ');
}
